import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { DirProposalType, DocumentDirectoryType } from '@econtractor/types';
import * as apiDocument from 'api/apiDocument';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import ESpinner from 'components/others/Spinner';
import SvgTrash from 'components/svg/SvgTrash';
import DocumentEditorOffcanvas from './DocumentEditorOffcanvas';
import DocumentCard from './DocumentCard';
import DocumentFilterType, { type DocumentTypeFilter } from './DocumentFilterType';
import SolarIcon from 'components/icons/SolarIcon';

const DOCUMENT_TYPE_ORDER: Record<DocumentDirectoryType, number> = {
    contract: 0,
    invoice: 1,
    act: 2,
    other: 3,
};

const sortDocuments = (items: DirProposalType[]) =>
    [...items].sort((a, b) => {
        const typeA = (a.proposalType || 'other') as DocumentDirectoryType;
        const typeB = (b.proposalType || 'other') as DocumentDirectoryType;
        const orderA = DOCUMENT_TYPE_ORDER[typeA] ?? 99;
        const orderB = DOCUMENT_TYPE_ORDER[typeB] ?? 99;
        if (orderA !== orderB) return orderA - orderB;
        return Number(b.proposalUpdated || 0) - Number(a.proposalUpdated || 0);
    });

const Documents = () => {
    const { t } = useTranslation(['Document', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const { auth } = useAuth();
    const [loading, setLoading] = useState(false);
    const [documents, setDocuments] = useState<DirProposalType[] | null>(null);
    const [editDocumentId, setEditDocumentId] = useState<number | null>(null);
    const [filterType, setFilterType] = useState<DocumentTypeFilter>('all');
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');

    const fetchDocuments = async () => {
        setLoading(true);
        const result = await apiDocument.getDirList({
            order: 'ORDER BY proposal_updated DESC',
            onPage: 999999,
            page: 1,
        });
        setLoading(false);
        if (!result) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        setDocuments(result.documents || []);
    };

    const deleteDocument = async (documentId: number) => {
        const stat = await apiDocument.deleteDirDocument(documentId);
        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        showToast(t('messDeleted'), 'success');
        await fetchDocuments();
    };

    const handleDelete = (document: DirProposalType) => {
        const name = document.proposalName || t('card.noName');
        const params = {
            buttons: [...getButtonsNoYesDanger(t)],
            type: 'confirm' as const,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('confirmDelete')}</span>
                    </h4>
                    <h5 className="mb-0">{name}</h5>
                    {document.proposalComment ? (
                        <small className="form-text text-muted">{document.proposalComment}</small>
                    ) : null}
                </>
            ),
        };
        params.buttons[1].handleClick = () => {
            showDlg(false);
            void deleteDocument(document.ID);
        };
        showDlg(params);
    };

    const handleDuplicate = (documentId: number) => {
        setEditDocumentId(-documentId);
    };

    useEffect(() => {
        if (!auth?.user?.admin) return;
        void fetchDocuments();
    }, [auth?.user?.admin]);

    const visibleDocuments = useMemo(() => {
        if (!documents) return [];
        const filtered =
            filterType === 'all'
                ? documents
                : documents.filter((doc) => (doc.proposalType || 'other') === filterType);
        return sortDocuments(filtered);
    }, [documents, filterType]);

    const empty = useMemo(
        () => !loading && documents !== null && visibleDocuments.length === 0,
        [loading, documents, visibleDocuments],
    );

    if (!auth?.user?.admin) {
        return (
            <Row>
                <Col sm={12}>
                    <Card>
                        <Card.Body>
                            <div className="alert alert-warning mb-0">{t('common:messReadOnly')}</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    }

    return (
        <>
            <DocumentEditorOffcanvas
                mode="dir"
                documentId={editDocumentId}
                onSaved={fetchDocuments}
                onClose={() => setEditDocumentId(null)}
            />

            <Row>
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                            <small className="text-muted">{t('subtitle')}</small>
                        </div>
                        <div className="mt-2 mt-sm-0">
                            <div className="page-toolbar-actions">
                                {canEdit ? (
                                    <Button className="fs-14" size="sm" variant="success" onClick={() => setEditDocumentId(0)}>
                                        <SolarIcon name="plus" className="fs-14 p-0" />
                                        <span className="ms-1">{t('toolCreate')}</span>
                                    </Button>
                                ) : null}
                                <Button size="sm" onClick={() => void fetchDocuments()}>
                                    <SolarIcon name="arrow-clockwise" className="fs-14 p-0" />
                                </Button>
                                <DocumentFilterType filterType={filterType} setFilterType={setFilterType} />
                            </div>
                        </div>
                    </div>
                </Col>

                <Col sm={12}>
                    {loading || documents === null ? <ESpinner /> : null}

                    {empty ? <div className="text-muted mt-2">{t('empty')}</div> : null}

                    {visibleDocuments.length ? (
                        <Row className="g-3 mt-1">
                            {visibleDocuments.map((document) => (
                                <Col key={document.ID} xs={12} md={6} xl={4}>
                                    <DocumentCard
                                        document={document}
                                        canEdit={canEdit}
                                        onEdit={setEditDocumentId}
                                        onDuplicate={handleDuplicate}
                                        onDelete={handleDelete}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ) : null}
                </Col>
            </Row>
        </>
    );
};

export default Documents;
